import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPIWrapper } from 'drf-react-by-schema';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { getDecimal } from 'src/utils/utils';

import { useAppSelector } from 'src/app/hooks';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

import { Layout } from 'src/styles';
import {
  AzeiteOrOleoProductionStatus,
  FlocosProductionStatus,
  GENERAL_ADMINS,
  getFlocosProductionLabel,
  getProcessingKeyLabel,
  getProductionKeyLabel,
  ProcessingKey,
  ProductionKey,
} from 'src/constants';
import { ProductionTimeRange } from 'src/constants/ProductionTimeRange';
import {
  getTotalsAdminLabel,
  getUsageTypesLabel,
  HomeTotalsAdminType,
  TotalsAdmin,
  TotalsAdminPendingProcessing,
  TotalsAdminPendingProduction,
  TotalsAdminProduction,
  UsageTypes,
} from 'src/@types';

import HomeTotalsCard from './HomeTotalsCard';
import HomeTotalsCardGroup from './HomeTotalsCardGroup';

export default function HomeTotalsAdmin() {
  const { perfil, isMobile } = useAppSelector((state) => state.page);
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);
  const { getRawData } = useAPIWrapper();
  const navigate = useNavigate();
  const location = useLocation();

  const [totals, setTotals] = useState<HomeTotalsAdminType>();
  const [timeRange, setTimeRange] = useState<ProductionTimeRange>(ProductionTimeRange.ALL);

  useEffect(() => {
    if (!perfil) {
      return;
    }
    const retrieveData = async () => {
      const newTotals = await getRawData(`totals_admin/?time_range=${timeRange}`);
      setTotals(newTotals);
    };
    retrieveData();
  }, [perfil, timeRange, location]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
  }, [isAdmin]);

  if (!perfil || !totals) {
    return null;
  }

  return (
    <Box sx={Layout.homeTotalsInner}>
      <Typography variant="h6" color="Menu" sx={{ textAlign: 'center' }}>
        Painel inicial
      </Typography>

      {Object.values(TotalsAdmin).map((adminKey) => {
        const hasTimeFilter = [TotalsAdmin.TOTALS_PRODUCED, TotalsAdmin.USAGE].includes(adminKey);
        return (
          <React.Fragment key={adminKey}>
            {!!totals[adminKey] && (
              <HomeTotalsCardGroup
                key={adminKey}
                label={getTotalsAdminLabel(adminKey)}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                hasTimeRangeMenu={hasTimeFilter}
                hasAllTimeRangeLabel={true}
                isMobile={isMobile}>
                {adminKey === TotalsAdmin.PENDENCIAS_PRODUCAO &&
                  Object.keys(totals[adminKey]).map((key) => {
                    const totalKey = key as keyof TotalsAdminPendingProduction;
                    const total = totals[adminKey][totalKey].length;
                    return (
                      <HomeTotalsCard
                        key={totalKey}
                        label={`${getProductionKeyLabel({ key: totalKey, isTitle: true })} em produção`}
                        value={total === 0 ? 'sem pendências' : getDecimal(total, 'lotes')}
                        seeMoreTarget={
                          key === 'flocos'
                            ? `/${totalKey}_production/?status=${FlocosProductionStatus.IN_PRODUCTION}`
                            : `/${totalKey}_production/?status=${AzeiteOrOleoProductionStatus.READY_TO_PROCESS}`
                        }
                        isMobile={isMobile}
                      />
                    );
                  })}
                {adminKey === TotalsAdmin.PENDENCIAS_BENEFICIAMENTO &&
                  Object.keys(totals[adminKey]).map((key) => {
                    const totalKey = key as keyof TotalsAdminPendingProcessing;
                    const total = totals[adminKey][totalKey];
                    return (
                      <HomeTotalsCard
                        key={totalKey}
                        label={`${getFlocosProductionLabel({ status: totalKey })}`}
                        value={getDecimal(
                          total,
                          getProductionKeyLabel({ key: ProductionKey.FLOCOS, unit: true })
                        )}
                        seeMoreTarget={`/flocos_production/?status=${totalKey}`}
                        isMobile={isMobile}
                      />
                    );
                  })}
                {adminKey === TotalsAdmin.TOTALS_PRODUCED &&
                  Object.keys(totals[adminKey]).map((key) => {
                    const totalKey = key as keyof TotalsAdminProduction;
                    const total = totals[adminKey][totalKey];
                    return (
                      <HomeTotalsCard
                        key={totalKey}
                        label={
                          totalKey === ProcessingKey.MESOCARPO
                            ? `${getProcessingKeyLabel(totalKey)}`
                            : `${getProductionKeyLabel({ key: totalKey, isTitle: true })}`
                        }
                        value={getDecimal(
                          total,
                          totalKey === ProcessingKey.MESOCARPO
                            ? 'Kg'
                            : getProductionKeyLabel({ key: totalKey, unit: true })
                        )}
                        seeMoreTarget={
                          totalKey === ProcessingKey.MESOCARPO
                            ? `/${totalKey}/?status=complete`
                            : `/${totalKey}_production/?status=complete`
                        }
                        isMobile={isMobile}
                      />
                    );
                  })}
                {adminKey === TotalsAdmin.USAGE &&
                  Object.keys(totals[adminKey]).map((key) => {
                    const totalKey = key as UsageTypes;
                    const total = totals[adminKey][totalKey];
                    return (
                      <HomeTotalsCard
                        key={totalKey}
                        label={getUsageTypesLabel(totalKey)}
                        value={getDecimal(total)}
                        seeMoreTarget={
                          [UsageTypes.ENTREPOSTO, UsageTypes.GRUPO].includes(totalKey)
                            ? `/${totalKey}`
                            : totalKey !== UsageTypes.USUARIA
                              ? `/gerenciar_usuarias/?tipo=${totalKey.replace('perfil_', '')}`
                              : '/gerenciar_usuarias'
                        }
                        isMobile={isMobile}
                      />
                    );
                  })}
              </HomeTotalsCardGroup>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// II - Pendências nos grupos
// 1. Azeite em produção
// 2. Óleo em produção
// 3. Flocos em produção

// II - Pendências no Entreposto
// 1. Flocos a revisar
// 2. Flocos revisados e não beneficiados

// I - Uso
// 1. usuárias
// 2. entrepostos
// 3. grupos
// 4. quebradeiras

// III - Produção
// 1. produção de mesocarpo
// 2. produção de azeite
// 3. produção de óleo
// 4. produção de flocos
